import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import fileDownload from 'js-file-download';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import { resolveContentDispositionFilename } from './resolve-content-disposition-filename';

// Static key identifier (the real URL is per-supplier, path-based).
export const downloadSupplierProductsSheetEndpointIdentifier =
  'ums/v1/cms/exclusive-deals/suppliers/products/sheet/download';

const buildEndpoint = (supplierId: number) =>
  `ums/v1/cms/exclusive-deals/suppliers/${supplierId}/products/sheet/download`;

type TData = void;
type TError = AxiosError;
type TQueryKey = [string, { supplierId: number | null }];
type Options = Omit<UseQueryOptions<void, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

/**
 * Download a supplier's public catalog (products + images) as an Excel sheet — the same
 * template the catalog upload consumes. `supplierId` is required (path).
 * Disabled by default — trigger via refetch().
 */
export function useDownloadSupplierProductsSheetApi(supplierId?: number | null, options?: Options): ReturnType {
  const toast = useCustomToast();
  const normalizedSupplierId = supplierId ?? null;

  const queryFn = async () => {
    if (normalizedSupplierId == null) return; // guarded by a disabled button, but be safe

    try {
      const response = await http.get(buildEndpoint(normalizedSupplierId), {
        responseType: 'arraybuffer',
      });

      const fallback = `supplier-${normalizedSupplierId}-products.xlsx`;
      const filename = resolveContentDispositionFilename(response.headers?.['content-disposition'], fallback);
      fileDownload(response.data, filename);
    } catch (error: any) {
      // Important to not show the toast in unauthorized case.
      if (!error.response?.status || error.response?.status === 401) throw error;

      // responseType is arraybuffer, so error.response.data is binary — show a generic message.
      toast({
        description: error.response?.status === 404 ? 'Supplier not found' : 'Failed to download the products sheet',
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper(
    [downloadSupplierProductsSheetEndpointIdentifier, { supplierId: normalizedSupplierId }],
    queryFn,
    { enabled: false, ...options }
  );
}
